import { DRFListResponseWrapper, getGlobalHeadersAsObject } from "..";
import { AssignmentStatus } from "../assignments/assignment";

export type Vote = {
	id: number;
	created: string;
	grade: string;
	vote_type: string;
	sub_type: string;
	person: number;
	user: number;
	assignment: number;
};

export type PersonVote = {
	id: number;
	created: string;
	grade: string;
	vote_type: string;
	sub_type: string;
	person: number;
	user: number;
	user_name: string;
	user_first_name?: string;
	assignment: number;
	assignment_name: string;
	/** The role's standing right now, not the standing it had when the vote was
	 *  cast. Both null when the vote carries no role, or one that was deleted. */
	assignment_status: AssignmentStatus | null;
	assignment_status_display: string | null;
	/** The pitch the recruiter approved in the draft-pitch dialog, and what the
	 *  dialog offered before they edited it. Both null when the vote carried no
	 *  draft, which is the overwhelming majority. */
	/** Graded, but the applicant is told nothing. Set from the reply dialog,
	 *  where the grader is looking at the text that would otherwise be sent.
	 *  Meaningless for a sourced candidate — it only suppresses the applicant's
	 *  own reply, never a pitch. */
	applicant_reply_suppressed?: boolean;
	pitch_text?: string | null;
	pitch_subject?: string | null;
	pitch_suggested_text?: string | null;
	pitch_suggested_subject?: string | null;
	is_text_edited?: boolean;
	pitch_channel?: "email" | "linkedin" | null;
	text_origin?: "template" | "ai" | "manual" | null;
	llm_prompt_template?: number | null;
	llm_prompt_template_name?: string | null;
};

export type VoteUpdatePayload = {
	assignment?: number;
	grade?: string;
	user?: number;
};

export const getVotes = (
	personId?: number,
	assignmentId?: number
): Promise<DRFListResponseWrapper<Vote[]>> => {
	const requestUrl = "/api/v2/votes/";
	const queryParams = [];

	if (personId) queryParams.push(["person_id", personId]);
	if (assignmentId) queryParams.push(["assignment_id", personId]);

	return fetch(`${requestUrl}?${new URLSearchParams(queryParams).toString()}`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => res.json());
};

export const getPersonVotesById = (
	id: number
): Promise<DRFListResponseWrapper<PersonVote[]>> => {
	if (!id || typeof id !== "number") {
		throw new Error(
			`getPersonEducationById called with invalid argument id '${id}'. Expected number`
		);
	}

	return fetch(`/api/v2/persons/${id}/vote/`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => res.json());
};

export const updateVote = async (
	voteId: number,
	payload: VoteUpdatePayload
): Promise<Vote> => {
	const response = await fetch(`/api/v2/vote2/${voteId}/`, {
		method: "PATCH",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify(payload),
	});

	if (!response.ok) {
		const errorData = await response.json().catch(() => ({}));
		throw new Error(
			errorData.detail || `Failed to update vote: ${response.status}`
		);
	}

	return response.json();
};

export const deleteVote = async (voteId: number): Promise<void> => {
	const response = await fetch(`/api/v2/vote2/${voteId}/`, {
		method: "DELETE",
		headers: getGlobalHeadersAsObject(),
	});

	if (!response.ok) {
		const errorData = await response.json().catch(() => ({}));
		throw new Error(
			errorData.detail || `Failed to delete vote: ${response.status}`
		);
	}
};
